import { authModule } from '@/auth/auth-module';
import { getRumToken } from '@/auth/rum-token';
import { getShellAuthProvider, resolveClientIdStrict } from '@/auth/auth-provider';
import { useClientStore, CLIENT_STORAGE_KEYS } from '@/client/client-store';
import { scopesForUrl } from './scope-map';
import { raptorAuthForUrl } from './raptor-auth-map';

const CLIENT_SCOPED_PATH_RE =
  /^\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\/app-container(\/|$)/i;

/**
 * Resolve the active clientId used to mint the RUM token for a
 * `Raptor-Authorization` header. Falls back through progressively more
 * durable sources so the header is attached even on a HARD REFRESH:
 *
 *   1. `?clientId=` on the request URL (e.g. the app-tiles call).
 *   2. `tempClientId` (localStorage) — the legacy `RumInterceptor` direct-nav
 *      pin set by `withPinnedClient` during the clientless role-mapping fetch.
 *   3. The live client store (`current`), once hydrated.
 *   4. The clientId in the browser URL path (`/{clientId}/app-container/...`).
 *   5. Legacy storage (`currentlySelectedClientId`).
 *
 * On refresh the route `beforeLoad` (→ `GetRolesMapping`) runs BEFORE
 * `useClients` hydrates the store, so `store.current` is null. Without the
 * pin / URL-path / storage fallbacks the mapping request goes out with no
 * `Raptor-Authorization` header → the gateway 401s ("No Raptor token present
 * in header") and the guard bounces the user Home.
 */
export function resolveRaptorClientId(safeUrl: URL): string | undefined {
  const fromQuery = safeUrl.searchParams.get('clientId') ?? safeUrl.searchParams.get('clientid');
  if (fromQuery) return fromQuery;

  // Legacy RumInterceptor direct-navigation pin (set by `withPinnedClient`
  // during the clientless role-mapping fetch) — resolves before store hydration.
  try {
    const pinned = localStorage.getItem('tempClientId');
    if (pinned) return pinned;
  } catch {
    /* storage unavailable */
  }

  const fromStore = useClientStore.getState().current?.id;
  if (fromStore) return fromStore;

  if (typeof window !== 'undefined') {
    const pathMatch = CLIENT_SCOPED_PATH_RE.exec(window.location.pathname);
    if (pathMatch?.[1]) return pathMatch[1];
  }

  try {
    // Client identity is localStorage-only in this shell (never session-scoped),
    // matching the legacy Angular shell — see `use-clients.readSavedClientId`.
    return localStorage.getItem(CLIENT_STORAGE_KEYS.localCurrent) ?? undefined;
  } catch {
    return undefined;
  }
}

/**
 * Allow-list of API origins our shell may call. Computed once from build-time
 * env so we never issue a request to an attacker-controlled URL even if a
 * caller passes one in (defense-in-depth against SSRF / open-redirect-style
 * abuse of `http()`).
 *
 * Includes:
 *   - VITE_RAPTOR_APIM_BASE_URL  (RUM / token service)
 *   - VITE_API_BASE_URL          (web API)
 *
 * Anything outside this list throws before `fetch()` is reached.
 */
const ALLOWED_API_ORIGINS: ReadonlySet<string> = new Set(
  [import.meta.env.VITE_RAPTOR_APIM_BASE_URL, import.meta.env.VITE_API_BASE_URL]
    .filter((v): v is string => typeof v === 'string' && v.length > 0)
    .map((v) => {
      try {
        return new URL(v).origin;
      } catch {
        return '';
      }
    })
    .filter(Boolean),
);

function assertAllowedUrl(url: string): URL {
  let parsed: URL;
  try {
    parsed = new URL(url, window.location.origin);
  } catch {
    throw new HttpError(`Invalid URL: ${url}`, 0, url, null);
  }
  if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') {
    throw new HttpError(`Disallowed protocol: ${parsed.protocol}`, 0, url, null);
  }
  // Same-origin requests (relative paths) are always allowed.
  if (parsed.origin === window.location.origin) return parsed;
  if (!ALLOWED_API_ORIGINS.has(parsed.origin)) {
    throw new HttpError(`Origin not in allow-list: ${parsed.origin}`, 0, url, null);
  }
  return parsed;
}

export interface HttpRequestInit extends Omit<RequestInit, 'body'> {
  /** Skip Authorization header (e.g. for public endpoints). */
  anonymous?: boolean;
  /** JSON-serialized body. Mutually exclusive with `body`. */
  json?: unknown;
  /** Override scopes; otherwise resolved from URL via `scopesForUrl()`. */
  scopes?: string[];
  /** Raw body (passed through to fetch). */
  body?: BodyInit | null;
}

export class HttpError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly url: string,
    readonly responseBody: unknown,
  ) {
    super(message);
    this.name = 'HttpError';
  }
}

/**
 * Thin auth-aware `fetch` wrapper.
 *
 * - Resolves an MSAL access token via `shell.auth.getTokenAsync` and attaches
 *   it as `Authorization: Bearer <token>` unless `anonymous: true`.
 * - Serializes `json` to body and sets `Content-Type` automatically.
 * - Throws `HttpError` for non-2xx responses; parses JSON for 2xx.
 *
 * Use through TanStack Query for caching / retry / dedup. This wrapper is
 * intentionally minimal — no retry, no interceptor chain.
 */
export async function http<T = unknown>(url: string, init: HttpRequestInit = {}): Promise<T> {
  const safeUrl = assertAllowedUrl(url);
  const { anonymous, json, scopes, headers: rawHeaders, ...rest } = init;

  const headers = new Headers(rawHeaders);

  if (!anonymous) {
    const tokenScopes = scopes ?? scopesForUrl(url);
    // Shell HTTP follows the SHELL SESSION provider (PRTL-2857). In MSAL mode
    // (default) it is pinned to MSAL: the shell's own RUM/APIM calls must never
    // flip onto an Auth0 token just because the current route is an Auth0 MFE —
    // only the per-MFE token path (`getAccessTokenforMFE`) is route-scoped. In
    // the independent Auth0 shell mode (VITE_AUTH_PROVIDER=auth0) there is no
    // MSAL session, so the shell uses the Auth0 token (paramclientid is attached
    // when a client is resolvable; the initial client-list call has none, and
    // the `/oauth/token` fetch patch backfills it once a client is selected).
    const token =
      getShellAuthProvider() === 'auth0'
        ? await authModule.getTokenAsync(tokenScopes, {
            provider: 'auth0',
            paramclientid: resolveClientIdStrict() ?? undefined,
          })
        : await authModule.getTokenAsync(tokenScopes, { provider: 'msal' });
    headers.set('Authorization', `Bearer ${token}`);

    // Some EY APIM backends (portalapps, role-scr-mgmt, …) require a RUM token
    // in `Raptor-Authorization` ON TOP of the Azure AD bearer — legacy attaches
    // it via `RumInterceptor`. Without it the gateway returns 401. The clientId
    // comes from the URL query when present (e.g. the app-tiles call carries
    // `?clientId=`), else the active client in the store (the role-mapping call
    // has no clientId param — legacy reads it from the client service too).
    const raptorAuth = raptorAuthForUrl(safeUrl.toString());
    if (raptorAuth && !headers.has('Raptor-Authorization')) {
      const clientId = resolveRaptorClientId(safeUrl);
      if (clientId) {
        try {
          const rumToken = await getRumToken(clientId, raptorAuth.scopes);
          if (rumToken) headers.set('Raptor-Authorization', rumToken);
        } catch {
          // Best-effort: if the RUM token can't be fetched, let the request
          // proceed (the API will 401) rather than throwing a different error.
        }
      }
    }
  }

  let body = init.body ?? null;
  if (json !== undefined) {
    body = JSON.stringify(json);
    if (!headers.has('Content-Type')) headers.set('Content-Type', 'application/json');
  }

  const res = await fetch(safeUrl, { ...rest, headers, body });

  const text = await res.text();
  let parsed: unknown = text;
  if (text && headers.get('Accept') !== 'text/plain') {
    try {
      parsed = JSON.parse(text);
    } catch {
      // Leave as text — caller may want raw.
    }
  }

  if (!res.ok) {
    throw new HttpError(`${res.status} ${res.statusText}`, res.status, url, parsed);
  }

  return parsed as T;
}
